import type {
    ExtendedMaterial,
    ExtendedMaterialOnTheFly,
    Material,
    CompositionComponent,
    MaterialOnTheFly,
} from '../gql';

import { ComponentType } from '../gql';

type StandardMaterial = Omit<Material, 'materialComposition'> & {
    materialComposition: [
        Omit<CompositionComponent, 'type'> & { type: ComponentType.Standard },
    ];
};

type ZipperMaterial = Omit<Material, 'materialComposition'> & {
    materialComposition: [
        Omit<CompositionComponent, 'type'> & {
            type: ComponentType.ZipperTeeth;
        },
        Omit<CompositionComponent, 'type'> & { type: ComponentType.ZipperTape },
        Omit<CompositionComponent, 'type'> & {
            type: ComponentType.ZipperSlider;
        },
    ];
};

type AllMaterialTypes =
    | ExtendedMaterial
    | ExtendedMaterialOnTheFly
    | Material
    | MaterialOnTheFly
    | undefined;

const isExtendedMaterialOnTheFly = (
    material: AllMaterialTypes
): material is ExtendedMaterialOnTheFly => {
    if (!material) return false;
    return (
        !('materialPublicId' in material) &&
        //mofs in ext comp don't have weight so use it to filter them out
        !('weightValue' in material)
    );
};

const isMaterial = (material: AllMaterialTypes): material is Material => {
    if (!material) return false;
    return (
        !('materialOnTheFlyPublicId' in material) &&
        !!(material as Material).materialSizes
    );
};

const isExtendedMaterial = (
    material: AllMaterialTypes
): material is ExtendedMaterial => {
    if (!material) return false;
    return !('materialOnTheFlyPublicId' in material) && !isMaterial(material);
};

const isMaterialOnTheFly = (
    material: AllMaterialTypes
): material is MaterialOnTheFly => {
    if (!material) return false;
    return (
        !('materialPublicId' in material) &&
        !isExtendedMaterialOnTheFly(material)
    );
};

/**
 * This utility function takes the material and safely types it as StandardMaterial if possible
 */
const isStandardMaterial = (material: Material): material is StandardMaterial =>
    material.materialComposition.length === 1 &&
    material.materialComposition[0].type === ComponentType.Standard;

/**
 * This utility function takes the material and safely types it as ZipperMaterial if possible
 */
const isZipperMaterial = (material: Material): material is ZipperMaterial =>
    material.materialComposition.length === 3 &&
    material.materialComposition.some(
        (composition) => composition.type === ComponentType.ZipperTeeth
    ) &&
    material.materialComposition.some(
        (composition) => composition.type === ComponentType.ZipperTape
    ) &&
    material.materialComposition.some(
        (composition) => composition.type === ComponentType.ZipperSlider
    );

export {
    isExtendedMaterial,
    isExtendedMaterialOnTheFly,
    isMaterial,
    isMaterialOnTheFly,
    isStandardMaterial,
    isZipperMaterial,
};
export type { AllMaterialTypes };
